import type { PermissionMap } from '../api/types';

/**
 * Client-side permission evaluation mirroring the server's PermissionSet semantics. This only
 * drives UI affordances (navigation, buttons, route guards); the API enforces every rule again.
 *
 *  - schoolId undefined: held anywhere in the organization
 *  - schoolId null:      held organization-wide
 *  - schoolId string:    held for that school (organization-wide grants included)
 */
export function hasPermission(
  permissions: PermissionMap | null | undefined,
  key: string,
  schoolId?: string | null,
): boolean {
  if (!permissions) return false;
  if (permissions.organization.includes(key)) return true;
  if (schoolId === null) return false;
  if (schoolId === undefined) {
    return Object.values(permissions.schools).some((list) => list.includes(key));
  }
  return permissions.schools[schoolId]?.includes(key) ?? false;
}

export interface PermissionRequirement {
  /** At least one of these must be held (anywhere). */
  anyOf?: readonly string[];
  /** All of these must be held (anywhere). Sensitive areas combine a base + sensitive permission. */
  allOf?: readonly string[];
  platformAdmin?: boolean;
}

export function satisfies(
  permissions: PermissionMap | null | undefined,
  requirement: PermissionRequirement | undefined,
  isPlatformAdmin = false,
): boolean {
  if (!requirement) return true;
  if (requirement.platformAdmin && !isPlatformAdmin) return false;
  if (
    requirement.anyOf &&
    requirement.anyOf.length > 0 &&
    !requirement.anyOf.some((p) => hasPermission(permissions, p))
  ) {
    return false;
  }
  if (requirement.allOf && !requirement.allOf.every((p) => hasPermission(permissions, p)))
    return false;
  return true;
}
